import { useParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  Title,
  Box,
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Center,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { IconPlus, IconEdit, IconInfoCircle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

import usePartner from '../hooks/partners/usePartner.ts';
import useContracts from '../hooks/contracts/useContracts.ts';
import useUpdatePartner from '../hooks/partners/useUpdatePartner.ts';

import type { UpdatePartnerForm } from '../components/modal/partner/UpdatePartnerModal.tsx';
import UpdatePartnerModal from '../components/modal/partner/UpdatePartnerModal.tsx';

import ContractTable from '../components/table/ContractTable.tsx';
import { SupportStoreTypeBadges } from '../components/ui/SupportStoreTypeBadges.tsx';
import PeopleManagementCard from '../components/ui/PeopleManagementCard.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import { notifySuccess } from '../utils/NotifySuccess.tsx';

export default function PartnerDetailPage() {
  // resource metadata
  const queryClient = useQueryClient();
  const { state } = useLocation();
  const { id: partnerId } = useParams();

  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { data: contracts = [], isLoading: isContractsLoading } =
    useContracts(partnerId);

  const { mutate: updatePartner, isPending: isUpdatePartnerPending } =
    useUpdatePartner();

  const [
    updatePartnerModalOpened,
    { open: openUpdatePartnerModal, close: closeUpdatePartnerModal },
  ] = useDisclosure(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // UI states
  const [closeDeletedNotice, setCloseDeletedNotice] = useState(false);

  // return if data still loading
  if (isPartnerLoading || isContractsLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // consts
  const displayDeletedNotice: boolean =
    state?.deleted === true && !closeDeletedNotice;

  // callback functions
  function onClickEditButton() {
    openUpdatePartnerModal();
  }

  function handleConfirmUpdatePartner({
    formValues,
  }: {
    formValues: UpdatePartnerForm;
  }) {
    if (isUpdatePartnerPending) return;

    updatePartner(
      { id: partnerId, ...formValues },
      {
        onSuccess: () => {
          closeUpdatePartnerModal();
          notifySuccess('更新業者成功');
          queryClient.invalidateQueries({
            queryKey: ['partner', partnerId],
          });
        },
      },
    );
  }

  return (
    <>
      {updatePartnerModalOpened && partner && (
        <UpdatePartnerModal
          opened={updatePartnerModalOpened}
          onClose={closeUpdatePartnerModal}
          onConfirm={handleConfirmUpdatePartner}
          partner={partner}
          isPending={isUpdatePartnerPending}
        />
      )}
      <PreviousPageButton href={`/partners`} content="返回業者管理列表" />
      {displayDeletedNotice && (
        <Alert
          variant="light"
          color="green"
          mb="lg"
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setCloseDeletedNotice(true)}
          title={state?.message}
        />
      )}

      <Stack gap="lg">
        <Paper radius="lg" p="xl" shadow="xs">
          <Group justify="space-between">
            <Box>
              <Group gap="xs">
                <Title order={3}>{partner.name}</Title>
                <SupportStoreTypeBadges
                  supportStoreTypes={partner.supportStoreTypes}
                />
              </Group>
              <Text size="xs" c="gray">
                {partner.displayNameForHuman}
              </Text>
            </Box>
            <Button
              variant="light"
              leftSection={<IconEdit size={20} />}
              onClick={onClickEditButton}
            >
              編輯
            </Button>
          </Group>
        </Paper>

        <ContractTable
          partnerId={partnerId}
          contracts={contracts}
          isPending={isContractsLoading}
        />

        <PeopleManagementCard partnerId={partnerId} />

        <Paper radius="lg" shadow="xs">
          <Group justify="space-between" p="xl">
            <Title order={3}>匯款資訊</Title>
            <Button
              leftSection={<IconPlus size={20} />}
              onClick={() => confirm('敬請期待 > <')}
            >
              新增匯款資訊
            </Button>
          </Group>
          <Stack px="md" pb="xl">
            <Box h={100}>
              <Center>
                <Title order={3}>工程麻吉開發中......</Title>
              </Center>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </>
  );
}
